// src/hooks/useListNavigation.js
import { useState, useEffect } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { isAnyModalOpen } from './useModalDialog';

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

/**
 * Shared N (next) / P (previous) / O (open) / A (add-to-agenda) keyboard
 * navigation for a list of items, designed for AAC switch-scanning software
 * (Mind Express 5, Grid 3) to drive via keystrokes. Wraps at both ends.
 * Disabled while focus is inside a text input, matching AacButton's guard.
 */
export function useListNavigation(items, { onOpen, onToggleAgenda, getLabel, getId }) {
  const { announce } = useAccessibility();
  const [currentIndex, setCurrentIndex] = useState(0);

  const itemsKey = items.map(getId).join('|');

  useEffect(() => {
    setCurrentIndex(0);
  }, [itemsKey]);

  useEffect(() => {
    if (!items.length) return undefined;

    const handleKeyDown = (event) => {
      if (isAnyModalOpen()) return;
      if (isTypingTarget(document.activeElement)) return;
      const key = event.key.toLowerCase();

      if (key === 'n') {
        event.preventDefault();
        // Compute the next index directly rather than via setCurrentIndex's
        // functional-updater form — `currentIndex` is already current here
        // (this handler is re-registered whenever it changes), and calling
        // announce() (itself another component's state setter) from inside
        // a state updater function is what triggered React's "Cannot
        // update a component while rendering a different component"
        // warning, since updater functions must be pure.
        const next = (currentIndex + 1) % items.length;
        setCurrentIndex(next);
        announce(getLabel(items[next]));
      } else if (key === 'p') {
        event.preventDefault();
        const next = (currentIndex - 1 + items.length) % items.length;
        setCurrentIndex(next);
        announce(getLabel(items[next]));
      } else if (key === 'o') {
        event.preventDefault();
        const current = items[currentIndex];
        if (current && onOpen) onOpen(current);
      } else if (key === 'a') {
        event.preventDefault();
        const current = items[currentIndex];
        if (current && onToggleAgenda) onToggleAgenda(current);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [items, currentIndex, onOpen, onToggleAgenda, getLabel, announce]);

  return {
    currentIndex,
    currentItem: items[currentIndex],
  };
}
